import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import FacebookLogin from 'react-facebook-login';
import { AuthService } from '../services/authService';
import { ApiService } from '../services/apiService';
import './AuthModal.css';

// Get Google Client ID from environment or use placeholder
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'signup' | 'signin';
  onModeChange: (mode: 'signup' | 'signin') => void;
  onUsernameSet?: () => void; // Callback when username is set
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode, onModeChange, onUsernameSet }) => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  // Username setup flow after social auth
  const [showUsernameSetup, setShowUsernameSetup] = useState(false);
  const [pendingAuthUserId, setPendingAuthUserId] = useState<string | null>(null);
  // Email verification flow
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  // Check if user is already signed in but not verified
  const [isSignedInButNotVerified, setIsSignedInButNotVerified] = useState(false);
  // Forgot password flow
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Check if user is already signed in but not verified when modal opens
  useEffect(() => {
    if (isOpen) {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser && ApiService.isAuthenticated()) {
        const isEmailVerified = currentUser.emailVerified || currentUser.email_verified || false;
        setIsSignedInButNotVerified(!isEmailVerified);
      } else {
        setIsSignedInButNotVerified(false);
      }
    }
  }, [isOpen]);

  // Validate username format (must match backend requirements)
  const validateUsername = (value: string): boolean => {
    // Username must be 6-20 characters, start with a letter, and contain only letters and numbers
    const usernameRegex = /^[a-zA-Z][a-zA-Z0-9]{5,19}$/;
    return usernameRegex.test(value);
  };

  // Check username availability with debouncing
  const checkUsernameAvailability = async (value: string) => {
    if (!value) {
      setUsernameError('');
      return;
    }

    if (!validateUsername(value)) {
      setUsernameError('Username must be 6-20 characters, start with a letter, and contain only letters and numbers');
      return;
    }
    
    setCheckingUsername(true);
    setUsernameError('');
    
    try {
      const result = await AuthService.checkUsername(value);
      if (!result.available) {
        setUsernameError('Username is already taken');
      } else {
        setUsernameError('');
      }
    } catch (err) {
      setUsernameError('Error checking username');
    } finally {
      setCheckingUsername(false);
    }
  };

  // Handle username change with debouncing
  const handleUsernameChange = (value: string) => {
    setUsername(value);
    
    // Clear existing timeout
    if (usernameCheckTimeout) {
      clearTimeout(usernameCheckTimeout);
    }
    
    // Set new timeout for checking
    if (value) {
      const timeout = setTimeout(() => {
        checkUsernameAvailability(value);
      }, 500);
      setUsernameCheckTimeout(timeout);
    } else {
      setUsernameError('');
      setCheckingUsername(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (mode === 'signup') {
        // Validate username
        if (!username || !validateUsername(username)) {
          setError('Username must be 6-20 characters, start with a letter, and contain only letters and numbers');
          setLoading(false);
          return;
        }
        
        if (usernameError) {
          setError('Please fix the username error');
          setLoading(false);
          return;
        }
        
        if (checkingUsername) {
          setError('Please wait while we check username availability');
          setLoading(false);
          return;
        }  
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
      // Validate password strength (must match backend requirements)
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!passwordRegex.test(password)) {
        setError('Password must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)');
        setLoading(false);
        return;
      }
    }

    try {
      if (mode === 'signup') {
        const result: any = await AuthService.signUp(email, password, username);
        if (result.error) {
          setError(result.error);
        } else if (result.verificationSent) {
          setSuccess('Account created! Verification email sent. Please check your inbox.');
          
          // CRITICAL: Reset auth state to reflect that user is signed out
          // This ensures UI shows "Sign In" instead of "Welcome, username"
          if (onUsernameSet) {
            onUsernameSet(); // This should trigger auth state refresh
          }
          
          // Reset form
          setEmail('');
          setUsername('');
          setPassword('');
          setConfirmPassword('');
          setUsernameError('');
          // Close modal after 3 seconds
          setTimeout(() => {
            onClose();
          }, 3000);
        }
      } else {
        const result: any = await AuthService.signIn(email, password);
        if (result.needsVerification) {
          setNeedsVerification(true);
          setPendingVerificationEmail(result.email);
          setError(result.error);
          
          // CRITICAL: Reset auth state since user was signed out
          if (onUsernameSet) {
            onUsernameSet(); // This should trigger auth state refresh
          }
        } else if (result.error) {
          setError(result.error);
        } else {
          setSuccess('Signed in successfully!');
          // Refresh auth state immediately
          if (onUsernameSet) {
            onUsernameSet(); // This will refresh auth state
          }
          // Close modal after 1 second
          setTimeout(() => {
            onClose();
          }, 1000);
        }
      }
    } catch (err: any) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  // Google OAuth login handler
  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!credentialResponse.credential) {
        setError('Google sign-in failed - no credential received');
        setLoading(false);
        return;
      }

      const result = await AuthService.oauthLogin('google', credentialResponse.credential);
      
      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      if (result.user) {
        // Check if user needs to set a custom username
        const uid = result.user.uid;
        const needsUsername = await AuthService.checkIfUserNeedsUsername(uid);
        if (needsUsername) {
          setPendingAuthUserId(uid);
          setUsername('');
          setShowUsernameSetup(true);
        } else {
          setSuccess('Signed in with Google successfully!');
          // Refresh auth state immediately
          if (onUsernameSet) {
            onUsernameSet(); // This will refresh auth state
          }
          setTimeout(() => {
            onClose();
          }, 1000);
        }
      }
    } catch (err: any) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed');
    setLoading(false);
  };

  // Facebook OAuth login handler
  const handleFacebookResponse = async (response: any) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (response.accessToken) {
        const result = await AuthService.oauthLogin('facebook', response.accessToken);
        
        if (result.error) {
          setError(result.error);
          setLoading(false);
          return;
        }

        if (result.user) {
          // Check if user needs to set a custom username
          const uid = result.user.uid;
          const needsUsername = await AuthService.checkIfUserNeedsUsername(uid);
          if (needsUsername) {
            setPendingAuthUserId(uid);
            setUsername('');
            setShowUsernameSetup(true);
          } else {
            setSuccess('Signed in with Facebook successfully!');
            // Refresh auth state immediately
            if (onUsernameSet) {
              onUsernameSet(); // This will refresh auth state
            }
            setTimeout(() => {
              onClose();
            }, 1000);
          }
        }
      } else {
        setError('Facebook sign-in failed');
      }
    } catch (err: any) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  const handleUsernameSetup = async () => {
    if (!pendingAuthUserId) return;
    // basic validation
    if (!username || !validateUsername(username)) {
      setUsernameError('Username must be 6-20 characters, start with a letter, and contain only letters and numbers');
      return;
    }
    if (checkingUsername) return;

    try {
      setLoading(true);
      const availability = await AuthService.checkUsername(username);
      if (!availability.available) {
        setUsernameError('Username is already taken');
        setLoading(false);
        return;
      }

      const { error } = await AuthService.updateUserUsername(pendingAuthUserId, username);
      if (error) {
        setError(error);
      } else {
        setShowUsernameSetup(false);
        setPendingAuthUserId(null);
        setSuccess('Username set successfully!');
        
        // Trigger auth state refresh
        if (onUsernameSet) {
          onUsernameSet();
        }
        
        setTimeout(() => {
          onClose();
        }, 800);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    // If user is already signed in, use the authenticated endpoint (no password needed)
    if (isSignedInButNotVerified && ApiService.isAuthenticated()) {
      setResendingEmail(true);
      setError('');
      setSuccess('');
      
      try {
        const result = await ApiService.resendVerificationEmail();
        if (result.success) {
          setSuccess('Verification email sent! Please check your inbox (and spam folder).');
        } else {
          setError(result.error || 'Failed to send verification email');
        }
      } catch (err: any) {
        setError('Failed to resend verification email');
      }
      
      setResendingEmail(false);
      return;
    }
    
    // Otherwise, require password (for sign-in flow)
    if (!pendingVerificationEmail || !password) {
      setError('Please enter your password to resend verification email');
      return;
    }
    
    setResendingEmail(true);
    setError('');
    
    try {
      const result = await AuthService.resendVerificationEmail(pendingVerificationEmail, password);
      if (result.success) {
        setSuccess('Verification email sent! Please check your inbox.');
      } else {
        setError(result.error || 'Failed to send verification email');
      }
    } catch (err: any) {
      setError('Failed to resend verification email');
    }
    
    setResendingEmail(false);
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setSendingReset(true);
    setError('');
    setSuccess('');

    try {
      const result = await AuthService.sendPasswordReset(resetEmail);
      if (result.success) {
        // Show the message from backend (which doesn't reveal if email exists)
        setSuccess(result.message || 'If an account with that email exists, a password reset link has been sent.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setSuccess('');
        }, 5000); // Show longer since message is generic
      } else {
        setError(result.error || 'Failed to send reset email');
      }
    } catch (err: any) {
      setError('An unexpected error occurred');
    }

    setSendingReset(false);
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2>{mode === 'signup' ? 'Sign Up' : 'Sign In'}</h2>
          <button className="auth-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="auth-modal-content">
          {success && (
            <div className="auth-success">
              {success}
            </div>
          )}

          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          {/* Show verification prompt for already-signed-in users */}
          {isSignedInButNotVerified && (
            <div className="verification-notice" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
              <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>📧 Email Verification Required</p>
              <p style={{ marginBottom: '15px' }}>Your email address hasn't been verified yet. Please verify your email to play online games.</p>
              <button 
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail}
                className="resend-btn"
                style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
              >
                {resendingEmail ? 'Sending...' : '📧 Resend Verification Email'}
              </button>
              <p className="small-text" style={{ fontSize: '0.85rem', color: '#666' }}>
                Check your inbox (and spam folder) for the verification email.
              </p>
            </div>
          )}

          {needsVerification && mode === 'signin' && !isSignedInButNotVerified && (
            <div className="verification-notice">
              <p>📧 Your email is not verified yet.</p>
              <button 
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail}
                className="resend-btn"
              >
                {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
              </button>
              <p className="small-text">You can play as guest while waiting for verification.</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email"
              />
            </div>
            {mode === 'signup' && (
              <div className="auth-form-group">
                <label htmlFor="username">
                  Username
                  {checkingUsername && <span className="checking-text"> (checking...)</span>}
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Choose a username"
                  minLength={3}
                  maxLength={20}
                  className={usernameError ? 'error' : ''}
                />
                {usernameError && (
                  <span className="username-error">
                    {usernameError}
                  </span>
                )}
                {!usernameError && username && !checkingUsername && validateUsername(username) && (
                  <span className="username-success">
                    ✓ Username is available
                  </span>
                )}
              </div>
            )}

            <div className="auth-form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter your password"
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {mode === 'signup' && (
              <div className="auth-form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="Confirm your password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (mode === 'signup' ? 'Sign Up' : 'Sign In')}
            </button>

            {/* Forgot Password Link - Only show in sign-in mode */}
            {mode === 'signin' && (
              <div className="forgot-password-section">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </button>
              </div>
            )}
          </form>

          {/* Social sign-up options - DISABLED FOR NOW
              To re-enable: 
              1. Set ENABLE_SOCIAL_AUTH to true below
              2. Configure OAuth credentials in environment variables
              3. Test Google/Facebook sign-up flows
          */}
          {false && ( // Change to ENABLE_SOCIAL_AUTH when ready
            <>
              <div className="auth-divider">
                <span>or</span>
              </div>

              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <div style={{ width: '100%' }}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    theme="outline"
                    size="large"
                    text="continue_with"
                    shape="rectangular"
                    locale="en"
                  />
                </div>
              </GoogleOAuthProvider>

              <FacebookLogin
                appId={FACEBOOK_APP_ID}
                autoLoad={false}
                fields="name,email,picture"
                callback={handleFacebookResponse}
                cssClass="auth-facebook-btn"
                icon={
                  <svg className="facebook-icon" viewBox="0 0 24 24" width="20" height="20" style={{ marginRight: '8px' }}>
                    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                }
                textButton="Continue with Facebook"
                disabled={loading}
              />
            </>
          )}

          <div className="auth-switch-mode">
            {mode === 'signup' ? (
              <p>
                Already have an account?{' '}
                <button 
                  type="button" 
                  onClick={() => onModeChange('signin')}
                  className="auth-mode-link"
                >
                  Sign In
                </button>
              </p>
            ) : (
              <p>
                Don't have an account?{' '}
                <button 
                  type="button" 
                  onClick={() => onModeChange('signup')}
                  className="auth-mode-link"
                >
                  Sign Up
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    {showUsernameSetup && (
      <div className="auth-modal-overlay" onClick={() => setShowUsernameSetup(false)}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <div className="auth-modal-header">
            <h2>Set Your Username</h2>
            <button className="auth-modal-close" onClick={() => setShowUsernameSetup(false)}>×</button>
          </div>
          <div className="auth-modal-content">
            <div className="auth-form-group">
              <label htmlFor="setup-username">
                Username {checkingUsername && <span className="checking-text">(checking...)</span>}
              </label>
              <input
                id="setup-username"
                type="text"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="Choose a username"
                minLength={3}
                maxLength={20}
                className={usernameError ? 'error' : ''}
                disabled={loading}
              />
              {usernameError && (
                <span className="username-error">{usernameError}</span>
              )}
            </div>

            <button
              className="auth-submit-btn"
              onClick={handleUsernameSetup}
              disabled={loading || !username || !!usernameError}
            >
              {loading ? 'Saving...' : 'Save Username'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Forgot Password Modal */}
    {showForgotPassword && (
      <div className="auth-modal-overlay" onClick={() => setShowForgotPassword(false)}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <div className="auth-modal-header">
            <h2>Reset Password</h2>
            <button className="auth-modal-close" onClick={() => setShowForgotPassword(false)}>×</button>
          </div>
          
          <div className="auth-modal-content">
            {success && (
              <div className="auth-success">
                {success}
              </div>
            )}

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            <p style={{ marginBottom: '15px', color: '#666', fontSize: '0.9rem' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <div className="auth-form-group">
              <label htmlFor="reset-email">Email Address</label>
              <input
                type="email"
                id="reset-email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={sendingReset}
              />
            </div>

            <button
              className="auth-submit-btn"
              onClick={handlePasswordReset}
              disabled={sendingReset}
            >
              {sendingReset ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              className="auth-mode-link"
              style={{ marginTop: '10px', width: '100%', textAlign: 'center' }}
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmail('');
                setError('');
                setSuccess('');
              }}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default AuthModal;
